import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";

export default function ItemMasterPage() {
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const pageGroupSize = 20;

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const role = String(currentUser?.role || "").toLowerCase();

  const isOperator = role === "operator";
  const isAdmin = role === "admin";

  const canAdd = isOperator || isAdmin;
  const canEdit = isOperator || isAdmin;
  const canDelete = isAdmin;

  async function loadData() {
    try {
      setLoading(true);
      const res = await api.get("/api/item-master", { params: { q } });
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setCurrentPage(1);
    } catch (err) {
      alert(err?.response?.data?.error || err.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  async function deleteItem(id) {
    const ok = window.confirm("Are you sure you want to delete this item?");
    if (!ok) return;

    try {
      await api.delete(`/api/item-master/${id}`);
      alert("Deleted successfully");
      loadData();
    } catch (err) {
      alert(err?.response?.data?.error || err.message || "Delete failed");
    }
  }

  const totalPages = Math.ceil(rows.length / pageSize);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage]);

  const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
  const startPage = currentGroup * pageGroupSize + 1;
  const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

  function goToPage(page) {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  }

  return (
    <div className="container-fluid" style={{ padding: 16 }}>
      <div className="card border-0 shadow-sm">
        <div className="card-body p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">Item Master</h2>

            {canAdd && (
              <button
                className="btn btn-primary"
                onClick={() => navigate("/item-master/add")}
              >
                + Add
              </button>
            )}
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2 align-items-center">
                <div className="col-md-8">
                  <input
                    className="form-control"
                    placeholder="Search by Item Code / Item Name / Item Type / Unit Name"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>

                <div className="col-md-2 d-grid">
                  <button className="btn btn-primary" onClick={loadData}>
                    Search
                  </button>
                </div>

                <div className="col-md-2 text-md-end">
                  {loading ? <span className="text-muted">Loading...</span> : null}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 110 }} className="text-center">
                        Serial No.
                      </th>
                      <th style={{ width: 90 }} className="text-center">
                        ID
                      </th>
                      <th style={{ width: 160 }}>Item Code</th>
                      <th style={{ minWidth: 260 }}>Item Name</th>
                      <th style={{ width: 160 }}>Item Type</th>
                      <th style={{ width: 160 }}>Unit Name</th>
                      <th style={{ width: 100 }} className="text-center">
                        Active
                      </th>
                      <th style={{ width: 220 }} className="text-center">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center p-4">
                          Loading...
                        </td>
                      </tr>
                    ) : paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center text-muted p-4">
                          No records found
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((r, i) => (
                        <tr key={r.id}>
                          <td className="text-center">
                            {(currentPage - 1) * pageSize + i + 1}
                          </td>
                          <td className="text-center">{r.id}</td>
                          <td>{r.item_code}</td>
                          <td>{r.item_name}</td>
                          <td>{r.item_type}</td>
                          <td>{r.unit_name || ""}</td>
                          <td className="text-center">
                            {Number(r.is_active) === 1 ? "Yes" : "No"}
                          </td>

                          <td>
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => navigate(`/item-master/view/${r.id}`)}
                              >
                                View
                              </button>

                              {canEdit && (
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => navigate(`/item-master/edit/${r.id}`)}
                                >
                                  Edit
                                </button>
                              )}

                              {canDelete && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => deleteItem(r.id)}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="card-footer">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
                  <div>
                    <b>Total: {rows.length}</b>
                  </div>

                  {totalPages > 0 && (
                    <div className="d-flex gap-1 flex-wrap justify-content-center">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Prev
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(startPage - 1)}
                        disabled={startPage === 1}
                      >
                        &laquo;
                      </button>

                      {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                        const page = startPage + i;
                        return (
                          <button
                            key={page}
                            className={`btn btn-sm ${
                              currentPage === page
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(endPage + 1)}
                        disabled={endPage >= totalPages}
                      >
                        &raquo;
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}